import React, { useMemo, useRef } from "react";
import { css, Theme, useTheme } from "@emotion/react";

import BreadcrumbList from "core/Components/Breadcrumb/BreadcrumbList";
import NavCountryDropdown from "core/Components/Navigations/NavCountryDropdown";
import useBreadcrumbs from "core/hooks/useBreadcrumbs";
import useBreakpoint from "core/hooks/useBreakpoint";
import { isBetween, isDown } from "theme/breakpoints";

type Props = {
    currentURLSegment?: string,
    countries?: [CountryItem],
    selectedCountry?: string,
    setCountryHandler: (countryCode: string) => void,
    hideHeaderCountry: boolean,
};

const BreadCrumbContainer: React.FC<Props> = ({
    currentURLSegment = "",
    countries = undefined,
    selectedCountry = '',
    setCountryHandler,
    hideHeaderCountry = false,
}) => {
    const theme: Theme = useTheme();
    const breadcrumbs = useBreadcrumbs();
    const { breakpoint, minWidth } = useBreakpoint();
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { breadcrumbHeight, breadcrumbBurgerHeight, sideMargin } = theme.sizes.menu;
    const { sizes: breakpointSizes } = theme.breakpoints;

    const headerWidth = useMemo(() => {
        if (isDown(breakpoint, 'md')) {
            return sideMargin;
        }
        if (isBetween(breakpoint, '3xl', '4xl')) {
            return breakpointSizes['3xl'];
        }
        return minWidth;
    }, [breakpoint, minWidth, breakpointSizes, sideMargin]);

    const style = css`
        position: relative;
        display: flex;
        justify-content: space-between;
        height: ${breadcrumbHeight}px;
        border-top: 1px solid rgba(0, 0, 0, 0.16);
        
        ${theme.breakpoints.up("5xl")} {
            padding-left: calc((100% - ${headerWidth}px) / 2 + 200px);
            padding-right: calc((100% - ${headerWidth}px) / 2 + 200px);
        }

        ${theme.breakpoints.between("xxl", "4xl")} {
            padding-left: calc((100% - ${headerWidth}px) / 2 + 80px);
            padding-right: calc((100% - ${headerWidth}px) / 2 + 80px);
        }

        ${theme.breakpoints.only("xl")} {
            padding-left: calc((100% - ${headerWidth}px) / 2 + 38px);
            padding-right: calc((100% - ${headerWidth}px) / 2 + 38px);
        }

        ${theme.breakpoints.down("lg")} {
            padding-left: ${sideMargin}px;
            height: ${breadcrumbBurgerHeight}px;
        }
    `;

    const breadcrumbStyle = css`
        position: relative;
        display: flex;
        padding-right: 10px;
        align-items: center;
        width: calc(100% - ${dropdownRef?.current?.offsetWidth || 0}px);
        font-size: 12px;
        
        ${theme.breakpoints.down('xs')} {
            width: calc(100% - ${sideMargin / 2}px);
        }
    `;

    const countryDropdownStyle = css`
        ${theme.breakpoints.down("xs")} {
            display: none;
        }
    `;

    return (
        <div css={style}>
            {(headerWidth > 0 && selectedCountry) && (
                <>
                    <BreadcrumbList
                        currentURLSegment={currentURLSegment}
                        breadcrumbs={breadcrumbs}
                        css={breadcrumbStyle}
                    />
                    {!hideHeaderCountry && (
                        <div css={countryDropdownStyle} ref={dropdownRef}>
                            <NavCountryDropdown
                                countries={countries}
                                selectedCountry={selectedCountry}
                                setCountryHandler={setCountryHandler}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default BreadCrumbContainer;